import { useEffect, useRef, useState, type PointerEvent } from "react";
import { api } from "../lib/api";

export interface MotionMask { x: number; y: number; width: number; height: number }
interface MotionSettings {
  cameraId: string;
  enabled: boolean;
  masks: MotionMask[];
  pixelDeltaThreshold: number;
  motionRatioThreshold: number;
  globalChangeRatio: number;
  startConsecutiveFrames: number;
  quietSeconds: number;
  mergeGapMs: number;
  algorithmVersion: string;
  updatedAt: string;
}

interface DragPoint { x: number; y: number }

export function MotionSettingsPanel({ cameraId, cameraName, onClose, onMessage }: {
  cameraId: string;
  cameraName: string;
  onClose: () => void;
  onMessage: (message: string) => void;
}) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [settings, setSettings] = useState<MotionSettings | null>(null);
  const [preview, setPreview] = useState("");
  const [dragStart, setDragStart] = useState<DragPoint | null>(null);
  const [dragEnd, setDragEnd] = useState<DragPoint | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let objectUrl = "";
    void Promise.all([
      api<MotionSettings>(`/api/cameras/${cameraId}/motion-settings`),
      fetch(`/api/cameras/${cameraId}/motion-preview`, { credentials: "same-origin" }).then(async (response) => {
        if (!response.ok) throw new Error("현재 카메라 화면을 불러오지 못했습니다.");
        objectUrl = URL.createObjectURL(await response.blob());
        return objectUrl;
      })
    ]).then(([nextSettings, nextPreview]) => {
      setSettings(nextSettings);
      setPreview(nextPreview);
    }).catch((error) => onMessage(error instanceof Error ? error.message : "움직임 설정 조회 실패"));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [cameraId, onMessage]);

  const point = (event: PointerEvent<HTMLDivElement>): DragPoint => {
    const bounds = surfaceRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width)),
      y: Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height))
    };
  };
  const beginMask = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const next = point(event);
    setDragStart(next);
    setDragEnd(next);
  };
  const moveMask = (event: PointerEvent<HTMLDivElement>) => {
    if (dragStart) setDragEnd(point(event));
  };
  const finishMask = (event: PointerEvent<HTMLDivElement>) => {
    if (!settings || !dragStart) return;
    const end = point(event);
    const mask = normalizeMask(dragStart, end);
    if (mask.width >= 0.01 && mask.height >= 0.01) setSettings({ ...settings, masks: [...settings.masks, mask] });
    setDragStart(null);
    setDragEnd(null);
  };
  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const saved = await api<MotionSettings>(`/api/cameras/${cameraId}/motion-settings`, {
        method: "PUT",
        body: JSON.stringify(settings)
      });
      setSettings(saved);
      onMessage("움직임 분석 설정을 저장했습니다. 기존 녹화는 ‘전체 녹화 다시 분석’을 눌러 새 기준을 적용할 수 있습니다.");
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "움직임 분석 설정 저장 실패");
    } finally {
      setSaving(false);
    }
  };
  const reanalyze = async () => {
    if (!window.confirm(`${cameraName}의 기존 녹화 전체를 현재 기준으로 다시 분석합니까?`)) return;
    setSaving(true);
    try {
      const result = await api<{ queued: number; skipped: number }>(`/api/cameras/${cameraId}/motion-reanalyze`, { method: "POST", body: "{}" });
      onMessage(`기존 녹화 ${result.queued}개를 분석 대기열에 추가했습니다${result.skipped ? ` (${result.skipped}개는 이미 대기 중)` : ""}.`);
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "재분석 요청 실패");
    } finally {
      setSaving(false);
    }
  };

  const draftMask = dragStart && dragEnd ? normalizeMask(dragStart, dragEnd) : null;
  return <div className="panel mt-4 motion-settings-panel">
    <div className="panel-title"><div><h3>{cameraName} · 움직임 분석</h3><span>빨간 영역은 분석에서 제외됩니다.</span></div><button type="button" className="btn-close btn-close-white" onClick={onClose} /></div>
    {!settings || !preview ? <div className="loading-inline">현재 화면과 설정을 불러오는 중…</div> : <>
      <div className="motion-mask-editor" ref={surfaceRef} onPointerDown={beginMask} onPointerMove={moveMask} onPointerUp={finishMask} onPointerCancel={() => { setDragStart(null); setDragEnd(null); }}>
        <img src={preview} alt={`${cameraName} 움직임 제외 영역 설정`} draggable={false} />
        {settings.masks.map((mask, index) => <button key={`${mask.x}-${mask.y}-${index}`} type="button" className="motion-mask" style={maskStyle(mask)} onPointerDown={(event) => event.stopPropagation()} onClick={() => setSettings({ ...settings, masks: settings.masks.filter((_, itemIndex) => itemIndex !== index) })} title="이 제외 영역 삭제"><i className="bi bi-x-lg" /></button>)}
        {draftMask && <span className="motion-mask motion-mask-draft" style={maskStyle(draftMask)} />}
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
        <label className="form-check mb-0"><input className="form-check-input" type="checkbox" checked={settings.enabled} onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })} /><span className="form-check-label">움직임 분석 사용</span></label>
        <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" disabled={!settings.masks.length} onClick={() => setSettings({ ...settings, masks: [] })}>제외 영역 모두 지우기</button>
      </div>
      <details className="mt-3"><summary>고급 감지 기준</summary><div className="row g-2 mt-1">
        <NumberField label="픽셀 변화 기준" value={settings.pixelDeltaThreshold} min={1} max={255} step={1} onChange={(value) => setSettings({ ...settings, pixelDeltaThreshold: value })} />
        <NumberField label="움직임 비율" value={settings.motionRatioThreshold} min={0.001} max={0.5} step={0.001} onChange={(value) => setSettings({ ...settings, motionRatioThreshold: value })} />
        <NumberField label="전 화면 변화 상한" value={settings.globalChangeRatio} min={0.1} max={1} step={0.01} onChange={(value) => setSettings({ ...settings, globalChangeRatio: value })} />
        <NumberField label="시작 연속 프레임" value={settings.startConsecutiveFrames} min={2} max={3} step={1} onChange={(value) => setSettings({ ...settings, startConsecutiveFrames: value })} />
        <NumberField label="종료 대기 (초)" value={settings.quietSeconds} min={3} max={5} step={1} onChange={(value) => setSettings({ ...settings, quietSeconds: value })} />
        <NumberField label="사건 병합 간격 (ms)" value={settings.mergeGapMs} min={0} max={2000} step={100} onChange={(value) => setSettings({ ...settings, mergeGapMs: value })} />
      </div></details>
      <div className="small text-secondary mt-3">{settings.algorithmVersion} · 원본 수정 없이 320px, 초당 2프레임으로 분석</div>
      <div className="d-flex gap-2 mt-3"><button type="button" className="btn btn-outline-info" disabled={saving} onClick={() => void reanalyze()}>전체 녹화 다시 분석</button><button type="button" className="btn btn-primary ms-auto" disabled={saving} onClick={() => void save()}>{saving ? "처리 중…" : "설정 저장"}</button></div>
    </>}
  </div>;
}

function NumberField({ label, value, min, max, step, onChange }: { label: string; value: number; min: number; max: number; step: number; onChange: (value: number) => void }) {
  return <div className="col-md-4"><label>{label}</label><input className="form-control" type="number" value={value} min={min} max={max} step={step} onChange={(event) => onChange(Number(event.target.value))} /></div>;
}

function normalizeMask(start: DragPoint, end: DragPoint): MotionMask {
  return { x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), width: Math.abs(end.x - start.x), height: Math.abs(end.y - start.y) };
}

function maskStyle(mask: MotionMask) {
  return { left: `${mask.x * 100}%`, top: `${mask.y * 100}%`, width: `${mask.width * 100}%`, height: `${mask.height * 100}%` };
}
